/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';

import { useUniversalCreateAnalytics } from '../../../hooks';
import { withIntlProvider } from '../../../services';
import type { RenderableMenuItem, RenderableMenuSectionData } from '../../../types/menuV2';
import MenuItemV2, { isNativeRenderableMenuItem } from '../MenuItemV2';
import MenuSectionPrimitive, { MIN_ITEMS } from '../MenuSectionPrimitive';

const MAX_ITEMS = 10;

export interface RenderableMenuSectionProps {
	section: RenderableMenuSectionData;
	sectionIndex: number;
	sectionStartIndex: number;
	totalMenuItemsCount: number;
}

function RenderableMenuSectionInner({
	section,
	sectionIndex,
	totalMenuItemsCount,
	sectionStartIndex,
}: RenderableMenuSectionProps) {
	const [showAll, setShowAll] = useState(false);

	const sectionItems = section.items;
	const sectionItemsCount = sectionItems.length;
	const analyticsFired = useRef(false);
	const { fireNewMenuItemsViewed } = useUniversalCreateAnalytics();

	useEffect(() => {
		if (sectionItems.length > 0 && !analyticsFired.current) {
			analyticsFired.current = true;
		}

		if (fg('navx_uc_cross_app_creation_v2_enabled')) {
			const crossAppCreationEnabled = fg('navx_universal_create_cross_app_creation_enabled');

			// Extract all ATIs and types from items in the section
			const itemAtis = sectionItems.map((item) =>
				isNativeRenderableMenuItem(item) ? item.ati.toString() : item.ati || 'unknown',
			);

			const itemTypes = sectionItems.map((item) =>
				isNativeRenderableMenuItem(item) ? 'native' : item.type,
			);

			// Create single metadata object for this section
			const baseMetadata = {
				groupHeading: section.heading,
				groupIndex: sectionIndex,
				groupItemCount: section.items.length,
				itemAtis,
				itemTypes,
				showMoreButton: !!(section.foldable && section.items.length > MIN_ITEMS),
			};

			const sectionMetadata =
				section.type === 'appDefinedSection'
					? [
							{
								...baseMetadata,
								type: 'appDefinedSection' as const,
								personalize: !!(section as any).personalize,
							},
						]
					: [
							{
								...baseMetadata,
								type: 'suggestionSection' as const,
							},
						];

			fireNewMenuItemsViewed({
				crossAppCreationEnabled,
				totalNativeItems: totalMenuItemsCount,
				totalSections: 1,
				totalCrossAppItems: sectionItems.filter((item) => !isNativeRenderableMenuItem(item)).length,
				sectionMetadata,
			});
		}
	}, [
		section,
		sectionIndex,
		totalMenuItemsCount,
		sectionStartIndex,
		sectionItems,
		fireNewMenuItemsViewed,
	]);

	const items = useMemo(() => {
		if (!sectionItemsCount) {
			return [];
		}

		if (!section.foldable || sectionItemsCount <= MIN_ITEMS) {
			return sectionItems;
		}

		return sectionItems.slice(0, showAll ? MAX_ITEMS : MIN_ITEMS);
	}, [section.foldable, sectionItems, showAll, sectionItemsCount]);

	const toggleShowMore = useCallback(() => {
		setShowAll((prev) => !prev);
	}, [setShowAll]);

	if (!sectionItems?.length) {
		return null;
	}

	return (
		<MenuSectionPrimitive
			sectionIndex={sectionIndex}
			itemLength={sectionItems.length}
			foldable={section.foldable}
			heading={section.heading}
			showAll={showAll}
			toggleShowMore={toggleShowMore}
			isNative
		>
			{items.map((menuItem: RenderableMenuItem, index: number) => (
				<MenuItemV2
					item={menuItem}
					key={`menu-section-${sectionIndex}-item-${index}`}
					itemIndex={index}
					totalMenuItemsCount={totalMenuItemsCount}
					sectionStartIndex={sectionStartIndex}
					sectionItemsCount={sectionItemsCount}
				/>
			))}
		</MenuSectionPrimitive>
	);
}

export const RenderableMenuSection = withIntlProvider(RenderableMenuSectionInner);
